import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Truck, Award, Heart } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useBlock, useSite } from "../contexts/SiteContext";

const HERO_DEFAULTS = {
    eyebrow: "",
    title_line1: "",
    title_line2: "",
    title_italic: "",
    subheading: "",
    primary_cta_label: "Discover",
    primary_cta_link: "/shop",
    secondary_cta_label: "Read our story",
    secondary_cta_link: "/p/about",
    hero_image: "",
    note_eyebrow: "",
    note_text: "",
};

export default function Home() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const { settings } = useSite();

    const hero = useBlock("home.hero", HERO_DEFAULTS);
    const marquee = useBlock("home.marquee", { messages: [] });
    const cats = useBlock("home.categories", { eyebrow: "", title: "", items: [] });
    const featured = useBlock("home.featured", { eyebrow: "", title: "" });
    const story = useBlock("home.story", {
        eyebrow: "",
        title: "",
        body: "",
        cta_label: "",
        cta_link: "/shop",
        image_1: "",
        image_2: "",
    });

    useEffect(() => {
        api.get("/products?featured=true&limit=8")
            .then(({ data }) => setProducts(data.products))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="bg-obsidian" data-testid="home-page">
            {/* Announcement marquee */}
            {settings.announcement?.enabled && marquee.messages?.length > 0 && (
                <div className="overflow-hidden border-b border-border-dim bg-obsidian py-3">
                    <div className="marquee flex whitespace-nowrap">
                        {Array.from({ length: 2 }).map((_, i) => (
                            <div key={i} className="flex shrink-0 items-center gap-12 px-6 text-[11px] uppercase tracking-[0.4em] text-gold/80">
                                {marquee.messages.map((m, j) => (
                                    <span key={j} className="flex items-center gap-12">
                                        <span>{m}</span>
                                        <span className="text-heart">♥</span>
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Hero */}
            <section className="relative grain overflow-hidden">
                <div className="mx-auto grid max-w-[1480px] grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-12 lg:px-12 lg:py-32">
                    <div className="lg:col-span-7">
                        {hero.eyebrow && (
                            <p className="text-[11px] uppercase tracking-[0.4em] text-gold">
                                <Sparkles className="mr-2 inline h-3 w-3" />
                                {hero.eyebrow}
                            </p>
                        )}
                        <h1 className="mt-6 font-display text-4xl leading-[0.95] text-white sm:text-6xl lg:text-[88px]">
                            {hero.title_line1}
                            <br />
                            {hero.title_line2}
                            <br />
                            <span className="italic text-gold">{hero.title_italic}</span>
                        </h1>
                        {hero.subheading && (
                            <p className="mt-8 max-w-xl text-base leading-relaxed text-white/70">{hero.subheading}</p>
                        )}
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            {hero.primary_cta_label && (
                                <Link
                                    to={hero.primary_cta_link || "/shop"}
                                    data-testid="hero-shop-cta"
                                    className="shine inline-flex items-center gap-3 bg-gold px-8 py-4 text-[12px] uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-gold-light"
                                >
                                    {hero.primary_cta_label}
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                            )}
                            {hero.secondary_cta_label && (
                                <Link
                                    to={hero.secondary_cta_link || "/p/about"}
                                    className="text-[12px] uppercase tracking-[0.3em] text-white/70 underline-offset-8 hover:text-gold hover:underline"
                                >
                                    {hero.secondary_cta_label}
                                </Link>
                            )}
                        </div>
                        <div className="mt-14 grid grid-cols-3 gap-6 border-t border-border-dim pt-8">
                            {[
                                { icon: Award, label: "Heritage craft" },
                                { icon: Truck, label: "Worldwide ship" },
                                { icon: Heart, label: "Made to love" },
                            ].map((f) => (
                                <div key={f.label} className="flex items-center gap-3 text-xs text-white/60">
                                    <f.icon className="h-4 w-4 text-gold" />
                                    <span className="uppercase tracking-[0.2em]">{f.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="relative lg:col-span-5">
                        <div className="relative">
                            <div className="absolute -left-6 -top-6 h-full w-full border border-gold/30" />
                            {hero.hero_image && (
                                <img
                                    src={hero.hero_image}
                                    alt="LovinLife editorial"
                                    className="relative aspect-[4/5] w-full object-cover"
                                />
                            )}
                            {hero.note_text && (
                                <div className="absolute -bottom-6 -right-6 max-w-[260px] border border-border-dim bg-obsidian p-5">
                                    {hero.note_eyebrow && (
                                        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                                            {hero.note_eyebrow}
                                        </p>
                                    )}
                                    <p className="mt-2 font-display text-xl text-white">{hero.note_text}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Category bento */}
            {cats.items?.length > 0 && (
                <section className="mx-auto max-w-[1480px] px-6 py-24 lg:px-12">
                    <div className="mb-12 flex items-end justify-between">
                        <div>
                            {cats.eyebrow && <p className="text-[11px] uppercase tracking-[0.4em] text-gold">{cats.eyebrow}</p>}
                            <h2 className="mt-3 font-display text-3xl text-white sm:text-5xl">{cats.title}</h2>
                        </div>
                        <Link
                            to="/shop"
                            className="hidden text-[12px] uppercase tracking-[0.3em] text-white/70 hover:text-gold md:inline"
                        >
                            Explore all →
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                        {cats.items.map((c) => (
                            <Link
                                key={c.name}
                                to={c.href}
                                data-testid={`category-${c.name.toLowerCase()}`}
                                className="hover-lift group relative aspect-[3/4] overflow-hidden border border-border-dim bg-surface"
                            >
                                {c.image && (
                                    <img
                                        src={c.image}
                                        alt={c.name}
                                        className="absolute inset-0 h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100"
                                    />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
                                <div className="relative flex h-full flex-col justify-end p-8">
                                    <p className="text-[11px] uppercase tracking-[0.4em] text-gold/90">Collection</p>
                                    <h3 className="mt-2 font-display text-4xl text-white">{c.name}</h3>
                                    <p className="mt-3 max-w-xs text-sm text-white/70">{c.blurb}</p>
                                    <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-gold">
                                        Browse <ArrowRight className="h-3 w-3" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Featured products */}
            <section className="mx-auto max-w-[1480px] px-6 pb-24 lg:px-12">
                <div className="mb-12 flex items-end justify-between">
                    <div>
                        {featured.eyebrow && <p className="text-[11px] uppercase tracking-[0.4em] text-gold">{featured.eyebrow}</p>}
                        <h2 className="mt-3 font-display text-3xl text-white sm:text-5xl">{featured.title}</h2>
                    </div>
                </div>
                {loading ? (
                    <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="aspect-[4/5] animate-pulse bg-surface" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-4">
                        {products.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                )}
            </section>

            {/* Story strip */}
            {story.title && (
                <section className="border-y border-border-dim bg-surface py-24">
                    <div className="mx-auto grid max-w-[1480px] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2 lg:px-12">
                        <div>
                            {story.eyebrow && <p className="text-[11px] uppercase tracking-[0.4em] text-gold">{story.eyebrow}</p>}
                            <h2 className="mt-4 font-display text-3xl leading-tight text-white sm:text-5xl">{story.title}</h2>
                            {story.body && <p className="mt-6 max-w-xl text-base text-white/70">{story.body}</p>}
                            {story.cta_label && (
                                <Link
                                    to={story.cta_link || "/shop"}
                                    className="mt-8 inline-flex items-center gap-3 border border-gold px-6 py-3 text-[12px] uppercase tracking-[0.3em] text-gold transition-colors hover:bg-gold hover:text-obsidian"
                                >
                                    {story.cta_label}
                                </Link>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {story.image_1 && (
                                <img src={story.image_1} alt="" className="aspect-[3/4] w-full translate-y-8 object-cover" />
                            )}
                            {story.image_2 && (
                                <img src={story.image_2} alt="" className="aspect-[3/4] w-full object-cover" />
                            )}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
